import type { PaddleNamespace } from "../types/paddle";
import { track } from "@/utils/analytics";
import { recallCheckout } from "../utils/checkoutAttribution";

const PADDLE_JS_SRC = "https://cdn.paddle.com/paddle/v2/paddle.js";

let paddleScriptPromise: Promise<void> | null = null;
let initializedToken: string | null = null;

const handlePaddleEvent = (event: unknown): void => {
	if (!event || typeof event !== "object") return;
	const eventName = (event as { name?: unknown }).name;
	if (eventName !== "checkout.completed") return;

	track("checkout_completed", { ...recallCheckout(), provider: "paddle" });

	window.Paddle?.Checkout?.close?.();
	window.dispatchEvent(new CustomEvent("paddle:checkout-completed"));
};

const loadPaddleScript = async (): Promise<void> => {
	if (window.Paddle) return;
	if (!paddleScriptPromise) {
		paddleScriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement("script");
			script.src = PADDLE_JS_SRC;
			script.async = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error("No se pudo cargar Paddle.js"));
			document.head.appendChild(script);
		});
	}
	return paddleScriptPromise;
};

const initializePaddle = async (): Promise<PaddleNamespace> => {
	const token = (import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined)?.trim();
	if (!token) {
		throw new Error("VITE_PADDLE_CLIENT_TOKEN no está configurado");
	}

	await loadPaddleScript();
	if (!window.Paddle) {
		throw new Error("Paddle.js no disponible");
	}

	const envRaw = (import.meta.env.VITE_PADDLE_ENV as string | undefined)?.trim().toLowerCase();
	if (envRaw === "sandbox") {
		window.Paddle.Environment.set("sandbox");
	}

	if (initializedToken !== token) {
		window.Paddle.Initialize({
			token,
			eventCallback: handlePaddleEvent,
		});
		initializedToken = token;
	}

	return window.Paddle;
};

interface OpenPaddleCheckoutInput {
	priceId: string;
	email?: string;
	userId: string;
	locale?: string;
}

export const openPaddleCheckout = async ({
	priceId,
	email,
	userId,
	locale,
}: OpenPaddleCheckoutInput): Promise<void> => {
	const Paddle = await initializePaddle();
	Paddle.Checkout.open({
		settings: {
			displayMode: "overlay",
			locale: locale || "es",
		},
		customer: email ? { email } : undefined,
		customData: {
			usuario_id: userId,
		},
		items: [{ priceId, quantity: 1 }],
	});
};
